import {
  startMediaUpload,
  uploadMediaPart,
  finishMediaUpload,
  abortMediaUpload,
} from './uploadNoteMedia'
import { UPLOAD_CHUNK_BYTES } from './mediaSchemas'
import type { UploadedMedia } from './mediaSchemas'

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = reader.result as string
      resolve(dataUrl.slice(dataUrl.indexOf(',') + 1))
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

/**
 * Upload a file through the app server into S3 in ~8MB chunks, so any file
 * size works and progress can be reported. Aborts the S3 multipart upload on
 * failure so incomplete parts don't linger.
 */
export async function uploadMediaFile(
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<UploadedMedia> {
  const { key, uploadId } = await startMediaUpload({
    data: { fileName: file.name, contentType: file.type },
  })

  const totalParts = Math.max(1, Math.ceil(file.size / UPLOAD_CHUNK_BYTES))
  const parts: { partNumber: number; etag: string }[] = []
  try {
    for (let i = 0; i < totalParts; i++) {
      const chunk = file.slice(
        i * UPLOAD_CHUNK_BYTES,
        (i + 1) * UPLOAD_CHUNK_BYTES,
      )
      const dataBase64 = await blobToBase64(chunk)
      const { etag } = await uploadMediaPart({
        data: { key, uploadId, partNumber: i + 1, dataBase64 },
      })
      parts.push({ partNumber: i + 1, etag })
      onProgress?.((i + 1) / totalParts)
    }
    return await finishMediaUpload({ data: { key, uploadId, parts } })
  } catch (err) {
    try {
      await abortMediaUpload({ data: { key, uploadId } })
    } catch {
      // best effort — an orphaned multipart upload costs nothing visible
    }
    throw err
  }
}
